import { useLocale } from "../hooks/useLocale";
import { msg } from "../locales/translate";
import { GD } from "src/GD";
import FileSaver from "file-saver";
import { S_DECTA_FILES_POPUP, type TDectaFilePopupRequest } from "src/view/popup/DectaFilesPopup";
import Button from "@ui/button/Button";
import { IconDownload } from "@ui/icons/IconDownload";
import { HBox } from "@ui/box/Box";
import { useTheme } from "styled-components";
import { ITheme } from "@ui/theme/GlobalTheme";
import { notify } from "../tools/notify";
import { useState } from "react";
import { Spinner } from "@ui/spinner/Spinner";
import { prepareFileDownloadBlob } from "./fileDownload";
import { normalizeFileResponseBlob } from "./fileResponse";

export const getDectaFilePath = (path: string, fileName: string) => {
	const cleanPath = path.trim();
	if (!cleanPath) return fileName;

	const lastSegment = cleanPath.split(/[\\/]/).filter(Boolean).pop();
	if (lastSegment === fileName) return cleanPath;

	const separator = cleanPath.includes("\\") && !cleanPath.includes("/") ? "\\" : "/";
	return cleanPath.replace(/[\\/]+$/, "") + separator + fileName;
};

export const isPreviewable = (fileName: string) => {
	const ext = fileName.split(".").pop()?.toLowerCase();
	return ext === "csv" || ext === "xml" || ext === "json" || ext === "log" || ext === "txt" || ext === "txt-simple" || ext === "001" || ext === "pdf" || ext === "zip";
};

const showError = (message: string) => {
	notify.error(message, { autoClose: 4000 });
};

const extractBlobError = async (blob: Blob): Promise<string | null> => {
	if (blob.type === "application/json" || blob.size < 1024) {
		try {
			const text = await blob.text();
			const json = JSON.parse(text);
			if (json?.error?.message) return json.error.details || json.error.message;
		} catch { /* not JSON, actual file */ }
	}
	return null;
};

const assertFileBlob = async (blob?: Blob): Promise<Blob> => {
	if (!blob) throw new Error(msg("fileIsEmptyOrUnavailable"));
	const err = await extractBlobError(blob);
	if (err) throw new Error(err);
	return normalizeFileResponseBlob(blob);
};

export const loadDectaFileBlob = async (path: string): Promise<Blob> => {
	const resp = await GD.REQ_API.request({
		method: "processing/statementfile",
		data: { path },
		responseType: "blob",
	});
	if (resp?.error) throw new Error(resp.error.details || resp.error.message || msg("errorLoadingFile"));
	return assertFileBlob(resp?.data as Blob | undefined);
};

export const uploadDectaFile = async (path: string, file: File): Promise<void> => {
	const formData = new FormData();
	formData.append("path", path);
	formData.append("file", file);

	const resp = await GD.REQ_API.request({
		method: "processing/statementupload",
		data: formData,
	});
	const error = resp?.error ?? resp?.data?.error;
	if (error) throw new Error(error.details || error.message || msg("errorUploadingFile"));
};

export const loadArchiveFileBlob = async (id: number): Promise<Blob> => {
	const resp = await GD.REQ_API.request({
		method: "processing/archive/" + id,
		data: {},
		responseType: "blob",
	});
	if (resp?.error) throw new Error(resp.error.details || resp.error.message || msg("errorLoadingArchiveFile"));
	return assertFileBlob(resp?.data as Blob | undefined);
};

export const loadDectaFile = async (path: string, fileName: string) => {
	try {
		const sourceBlob = await loadDectaFileBlob(path);
		FileSaver.saveAs(await prepareFileDownloadBlob(fileName, sourceBlob), fileName);
	} catch (e) {
		showError(e instanceof Error ? e.message : msg("errorLoadingFile"));
	}
};

export const loadArchiveFile = async (id: number, fileName: string) => {
	try {
		const sourceBlob = await loadArchiveFileBlob(id);
		FileSaver.saveAs(await prepareFileDownloadBlob(fileName, sourceBlob), fileName);
	} catch (e) {
		showError(e instanceof Error ? e.message : msg("errorLoadingArchiveFile"));
	}
};

export const openDectaFilePopup = (request: TDectaFilePopupRequest) => {
	S_DECTA_FILES_POPUP.invoke(request);
};

export const ArchiveFileActions = ({ id, fileName, status }: { id: number; fileName: string; status: string }) => {
	useLocale();
	const theme = useTheme() as ITheme;
	const [busy, setBusy] = useState(false);
	const btnSx = { padding: "4px", borderRadius: "6px", "&:hover": { backgroundColor: theme.colors.backgroundPrimary } };
	const canDownload = status === "uploaded" || status === "completed";

	if (!canDownload) return null;

	return (
		<HBox gap="4px" sx={{ alignItems: "center" }}>
			<Button
				variant="icon"
				sx={btnSx}
				disabled={busy}
				onClick={async event => {
					event?.stopPropagation?.();
					if (busy) return;
					setBusy(true);
					await loadArchiveFile(id, fileName);
					setBusy(false);
				}}>
				{busy ? <Spinner size={20} color={theme.colors.textSecondary} /> : <IconDownload size={24} color={theme.colors.textSecondary} />}
			</Button>
		</HBox>
	);
};

export const DectaFileActions = ({ filePath, fileName }: { filePath: string; fileName: string }) => {
	useLocale();
	const theme = useTheme() as ITheme;
	const [busy, setBusy] = useState(false);
	const btnSx = { padding: "4px", borderRadius: "6px", "&:hover": { backgroundColor: theme.colors.backgroundPrimary } };
	const fullPath = getDectaFilePath(filePath, fileName);

	return (
		<HBox gap="4px" sx={{ alignItems: "center" }}>
			<Button
				variant="icon"
				sx={btnSx}
				disabled={busy}
				onClick={async event => {
					event?.stopPropagation?.();
					if (busy) return;
					setBusy(true);
					await loadDectaFile(fullPath, fileName);
					setBusy(false);
				}}>
				{busy ? <Spinner size={20} color={theme.colors.textSecondary} /> : <IconDownload size={24} color={theme.colors.textSecondary} />}
			</Button>
		</HBox>
	);
};
